import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Desktop dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      // Mobile panel - close if click is outside panel AND outside hamburger
      if (
        mobileOpen &&
        mobilePanelRef.current &&
        !mobilePanelRef.current.contains(e.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(e.target as Node)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  // Close menus on route change
  useEffect(() => {
    setDropdownOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleCategoryClick = () => {
    setDropdownOpen(false);
    setMobileOpen(false);
  };

  const handleHamburgerClick = () => {
    setMobileOpen((prev) => !prev);
  };

  return (
    <>
      {/* --- Navbar Bar --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 flex items-center justify-between">
          {/* Left: Brand */}
          <Link
            to="/"
            className="text-sm md:text-base font-semibold text-foreground-900 tracking-wide whitespace-nowrap hover:text-foreground-700 transition-colors cursor-pointer"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            allchinabuyspreadsheet.wtf
          </Link>

          {/* Center: Nav Links (desktop) */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/allchinabuyspreadsheet-guide"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              Shopping Guide
            </Link>
          </div>

          {/* Right: Categories Dropdown (desktop) + Mobile Toggle */}
          <div className="flex items-center gap-3">
            {/* Desktop Categories */}
            <div ref={dropdownRef} className="relative hidden md:block">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-300 text-sm font-medium text-foreground-700 bg-background-50 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-background-50 rounded-xl py-2 max-h-[420px] overflow-y-auto border border-background-200"
                  style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                >
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <span>{cat.name}</span>
                      <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <button
              ref={hamburgerRef}
              onClick={handleHamburgerClick}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full border border-foreground-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* --- Mobile Menu Panel (OUTSIDE nav to avoid backdrop-filter containing block issue) --- */}
      <div
        ref={mobilePanelRef}
        className={`md:hidden fixed top-14 left-0 right-0 bottom-0 z-40 bg-background-50 overflow-y-auto transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-y-0' : '-translate-y-full pointer-events-none'
        }`}
        aria-hidden={!mobileOpen}
      >
        <div className="px-5 py-6 space-y-6">
          {/* Nav Links */}
          <div className="space-y-1">
            <p className="text-xs text-foreground-400 uppercase tracking-wider mb-3">Navigation</p>
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-lg text-foreground-500"></i>
              Home
            </Link>
            <Link
              to="/allchinabuyspreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-book-read-line text-lg text-foreground-500"></i>
              Shopping Guide
            </Link>
          </div>

          {/* Divider */}
          <div className="border-t border-background-200"></div>

          {/* Categories */}
          <div>
            <p className="text-xs text-foreground-400 uppercase tracking-wider mb-3 px-1">Product Categories</p>
            <div className="grid grid-cols-1 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={handleCategoryClick}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-700 text-sm hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 flex-shrink-0">
                    <i className="ri-price-tag-3-line text-sm"></i>
                  </span>
                  <span className="flex-1">{cat.name}</span>
                  <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                </a>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-background-200"></div>

          {/* Quick CTAs */}
          <div className="space-y-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-6 py-3.5 border-2 border-primary-500 text-primary-500 rounded-full text-sm font-semibold hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
            >
              allchinabuy sheet
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Overlay backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 top-14 bg-black/20 z-30"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}